import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";

const WHATSAPP_NUMBER = "491728246564";
const ease = [0.22, 1, 0.36, 1];

export default function ContactSection() {
  return (
    <section id="kontakt" className="relative py-20 sm:py-28 bg-brand-paper overflow-hidden" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left — headline + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease }}
            className="lg:col-span-6"
          >
            <p className="text-[11px] font-bold tracking-[0.25em] text-brand-energy uppercase">Kontakt</p>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-brand-ink tracking-[-0.035em] leading-[0.95]">
              Persönliche Beratung<br />aus Herford – <br />
              <span className="text-brand-muted">deutschlandweit.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-brand-muted max-w-xl leading-relaxed">
              Fragen zu Strom oder Gas, Privat oder Gewerbe? Schreiben Sie uns direkt per WhatsApp oder rufen Sie an. Wir sind für Sie da.
            </p>

            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hallo Strombaron, ich hätte gerne eine Beratung.")}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => window.gtag && window.gtag("event", "whatsapp_click", { location: "contact_section" })}
              className="btn-primary mt-8 inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Beratung per WhatsApp
              <ArrowUpRight className="w-4 h-4 arrow-shift" strokeWidth={2.4} />
            </a>
          </motion.div>

          {/* Right — contact cards bento */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ContactCard icon={Mail} title="E-Mail" value="info@strombaron.de" href="mailto:info@strombaron.de" testId="contact-email" delay={0.05} />
            <ContactCard icon={Phone} title="Telefon" value="0172 824 6564" href="tel:+491728246564" testId="contact-phone" delay={0.12} />
            <ContactCard icon={MapPin} title="Standort" value="Westring 117, 32051 Herford, NRW" testId="contact-location" delay={0.19} />
            <ContactCard icon={MessageCircle} title="Öffnungszeiten" value="Mo–Fr · 09:00–18:00 Uhr" testId="contact-hours" delay={0.26} />
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon: Icon, title, value, href, testId, delay = 0 }) {
  const Tag = href ? motion.a : motion.div;
  return (
    <Tag
      {...(href ? { href } : {})}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease, delay }}
      whileHover={href ? { y: -4 } : {}}
      className="group relative bg-brand-surface border border-brand-line rounded-3xl p-6 hover:border-brand-ink transition-colors duration-300 block overflow-hidden"
      data-testid={testId}
    >
      <Icon className="w-5 h-5 text-brand-ink mb-4" strokeWidth={2.4} />
      <p className="text-[10px] uppercase font-bold tracking-[0.2em] text-brand-muted">{title}</p>
      <p className="mt-2 font-heading font-bold text-brand-ink text-base leading-snug">{value}</p>
      {href && (
        <ArrowUpRight className="absolute top-5 right-5 w-4 h-4 text-brand-line group-hover:text-brand-ink group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" strokeWidth={2.4} />
      )}
    </Tag>
  );
}
